import { ONE_DAY_MS } from '@eflight/shared/src/common/constants/time';
import Dexie from 'dexie';

import { type AircraftMaintenanceLimitExtension } from '../../Admin/AircraftMaintenanceLimitExtensions/AircraftMaintenanceLimitExtension';
import { type AircraftMaintenanceLimit } from '../../Admin/AircraftMaintenanceLimits/AircraftMaintenanceLimit';
import { type InternalContact } from '../../Admin/InternalContacts/types';
import {
    type CreateAircraftDocumentInput,
    type CreateLogbookRecordInput,
    type CreateSignedDataInput,
    type CreateSigningKeyChangeInput,
} from '../../API';
import { type ContactInput } from '../../APIExtensions';
import { type FinishLegState } from '../../screens/Flight/Leg/finish/types';
import { type LegDraft } from '../../screens/Flight/Leg/scratchpad';
import { type StartLegState } from '../../screens/Flight/Leg/start/types';
import { type ErrorLog } from '../../utils/ErrorLog/types';
import { type DownloadedDocument } from '../AircraftDocuments/types';
import { type Aircraft } from '../Aircrafts/types';
import { type AirportCoordinates } from '../AirportCoordinates/AirportCoordinateDatasource';
import { type CountableChange } from '../CountableChanges/types';
import { type DutyItem } from '../Duties/types';
import { type FlightDocument } from '../FlightDocuments/FlightDocument';
import { type LocalDocumentContent } from '../FlightDocuments/LocalDocumentContent';
import { type FlightExpenseRecord } from '../FlightExpenses/FlightExpense';
import { type FlightLeg } from '../FlightLegs/FlightLeg';
import { type Flight } from '../Flights/Flight';
import { type FlightSupplementalData } from '../FlightSupplementalData/FlightSupplementalDataTypes';
import { type ReleaseToService } from '../ReleaseToService/types';
import { type Squawk, type SquawkDeferral } from '../Squawks/types';
import { type SyncItem } from './syncHelpers';
import { type WithId } from './types';

export const LOCAL_DB_VERSION = 41;

export class LocalDB extends Dexie {
    aircrafts: Dexie.Table<Aircraft, string>;
    aircraftDocuments: Dexie.Table<CreateAircraftDocumentInput, string>;
    aircraftDocumentsDownloaded: Dexie.Table<DownloadedDocument, string>; // to be deprecated
    aircraftMaintenanceLimits: Dexie.Table<AircraftMaintenanceLimit, string>;
    aircraftMaintenanceLimitExtensions: Dexie.Table<AircraftMaintenanceLimitExtension, string>;
    flights: Dexie.Table<Flight, string>;
    legs: Dexie.Table<FlightLeg, string>;
    documents: Dexie.Table<FlightDocument, string>;
    flightExpenses: Dexie.Table<FlightExpenseRecord, string>;
    localDocumentContents: Dexie.Table<LocalDocumentContent, string>;
    countableChanges: Dexie.Table<CountableChange, string>;
    squawks: Dexie.Table<Squawk, string>;
    squawkDeferrals: Dexie.Table<SquawkDeferral, string>;
    releaseToServices: Dexie.Table<ReleaseToService, string>;
    dutyItems: Dexie.Table<DutyItem, string>;
    contacts: Dexie.Table<ContactInput, string>;
    logbookRecords: Dexie.Table<CreateLogbookRecordInput & WithId, string>;
    airportCoordinates: Dexie.Table<AirportCoordinates, string>;
    scratchStartLegState: Dexie.Table<LegDraft<StartLegState>, string>;
    scratchFinishLegState: Dexie.Table<LegDraft<FinishLegState>, string>;
    syncItems: Dexie.Table<SyncItem, string>;
    signingKeyChanges: Dexie.Table<CreateSigningKeyChangeInput, string>;
    signedData: Dexie.Table<CreateSignedDataInput, string>;
    initPromise: Promise<this>;
    errorLogs: Dexie.Table<ErrorLog, string>;
    flightSupplementalData: Dexie.Table<FlightSupplementalData, string>;
    internalContacts: Dexie.Table<InternalContact, string>;

    constructor() {
        super('alol_tlb');
        this.version(LOCAL_DB_VERSION).stores({
            flights: '&id,aircraftId,reservationId',
            aircrafts: '&id,userId',
            aircraftDocuments: '&id,aircraftId',
            aircraftDocumentsDownloaded: '&id,sourceId',
            aircraftMaintenanceLimits: '&id,aircraftId',
            aircraftMaintenanceLimitExtensions: '&id,maintenanceLimitId',
            airportCoordinates: '&icao,lat,lng',
            scratchStartLegState: '&id,flightId,aircraftId',
            scratchFinishLegState: '&id,flightId,aircraftId',
            legs: '&id,flightId,aircraftId,reservationId',
            countableChanges: '&id,aircraftId,flightId,[legId+type]',
            documents: '&id,flightId,aircraftId,[legId+phase],squawkId,releaseToServiceId,maintenanceRecordId,type',
            flightExpenses: '&id,flightId,legId',
            localDocumentContents: '&id',
            squawks: '&id,aircraftId,flightId,legId',
            squawkDeferrals: '&id,squawkId',
            releaseToServices: '&id,squawkId,aircraftId',
            dutyItems: '&id,userId',
            contacts: '&id,userId,crewId',
            logbookRecords: '&id,legId,contactId,crewId',
            signingKeyChanges: '&id',
            signedData: '&id',
            syncItems: '&id',
            errorLogs: '&id',
            flightSupplementalData: '&id,type,flightId,reservationId',
            internalContacts: '&id',
        });

        this.aircrafts = this.table('aircrafts');
        this.aircraftDocuments = this.table('aircraftDocuments');
        this.aircraftDocumentsDownloaded = this.table('aircraftDocumentsDownloaded');
        this.aircraftMaintenanceLimits = this.table('aircraftMaintenanceLimits');
        this.aircraftMaintenanceLimitExtensions = this.table('aircraftMaintenanceLimitExtensions');
        this.flights = this.table('flights');
        this.legs = this.table('legs');
        this.countableChanges = this.table('countableChanges');
        this.documents = this.table('documents');
        this.flightExpenses = this.table('flightExpenses');
        this.localDocumentContents = this.table('localDocumentContents');
        this.squawks = this.table('squawks');
        this.squawkDeferrals = this.table('squawkDeferrals');
        this.releaseToServices = this.table('releaseToServices');
        this.dutyItems = this.table('dutyItems');
        this.contacts = this.table('contacts');
        this.logbookRecords = this.table('logbookRecords');
        this.airportCoordinates = this.table('airportCoordinates');
        this.scratchStartLegState = this.table('scratchStartLegState');
        this.scratchFinishLegState = this.table('scratchFinishLegState');
        this.signingKeyChanges = this.table('signingKeyChanges');
        this.signedData = this.table('signedData');
        this.syncItems = this.table('syncItems');
        this.errorLogs = this.table('errorLogs');
        this.flightSupplementalData = this.table('flightSupplementalData');
        this.internalContacts = this.table('internalContacts');
        this.initPromise = this.init();
    }

    async init(): Promise<this> {
        this.cleanup().catch(console.error);

        return this;
    }

    /** Deletes local data nothing will use any more. */
    async cleanup(): Promise<void> {
        await Promise.all([this.purgeOldUploadedDocumentContents(), this.purgeOldLegDrafts()]);
    }

    // A leg draft not saved for 2 days is forgotten; otherwise it stays until its form is saved.
    async purgeOldLegDrafts(): Promise<void> {
        const now = new Date();
        const savedBefore = new Date(now.getTime() - 2 * ONE_DAY_MS);
        await Promise.all(
            [this.scratchStartLegState, this.scratchFinishLegState].map(
                async (drafts: Dexie.Table<LegDraft<object>>) => {
                    // a draft saved before the stamp existed gets its 2 days from now on
                    await drafts.filter((draft) => !draft.draftSavedAt).modify({ draftSavedAt: now });
                    await drafts.filter((draft) => (draft.draftSavedAt ?? now) < savedBefore).delete();
                }
            )
        );
    }

    // An uploaded document keeps its content on the device for a week of offline viewing, then shows
    // from storage; otherwise the device keeps every photo it ever took.
    async purgeOldUploadedDocumentContents(): Promise<void> {
        const createdBefore = new Date(Date.now() - 7 * ONE_DAY_MS);
        const ids = await this.documents
            .filter((document) => document.contentKey !== undefined && document.createdAt < createdBefore)
            .primaryKeys();
        await this.localDocumentContents.bulkDelete(ids);
    }

    async initComplete(): Promise<this> {
        return this.initPromise;
    }

    async wipe(): Promise<this> {
        await Promise.all([
            this.aircrafts.clear(),
            this.aircraftDocuments.clear(),
            this.aircraftDocumentsDownloaded.clear(),
            this.aircraftMaintenanceLimits.clear(),
            this.aircraftMaintenanceLimitExtensions.clear(),
            this.flights.clear(),
            this.legs.clear(),
            this.countableChanges.clear(),
            this.documents.clear(),
            this.flightExpenses.clear(),
            this.localDocumentContents.clear(),
            this.squawks.clear(),
            this.squawkDeferrals.clear(),
            this.releaseToServices.clear(),
            this.dutyItems.clear(),
            this.contacts.clear(),
            this.logbookRecords.clear(),
            this.airportCoordinates.clear(),
            this.scratchStartLegState.clear(),
            this.scratchFinishLegState.clear(),
            this.signingKeyChanges.clear(),
            this.signedData.clear(),
            this.syncItems.clear(),
            this.flightSupplementalData.clear(),
            this.internalContacts.clear(),
        ]);

        return this;
    }
}

let db: LocalDB | null = null;

export const getDb = (): LocalDB => {
    db ||= new LocalDB();

    return db;
};
